import uniq from "lodash/uniq";
import { UNDETERMINED_FUTURE } from "PFApp/constants/dates";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useBookingPastDaysLimit } from "PFApp/hooks";
import { utc } from "PFCore/helpers/date";
import { useBookingsForUpdate } from "PFCore/hooks/queries/bookings/bulk_operation/use_bookings_for_update";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { BulkOperationFilter, Exclude } from "PFCore/services/bookings_bulk_operation";
import { useEffect, useMemo } from "react";

import { useBookingEditModeStoreActions, useEditMode, useSelectModeState } from "../booking_edit_mode_store";
import { isEditModeSelectOrDraft } from "../utils/select_mode.utils";

export const useSelectModeData = () => {
  const editMode = useEditMode();
  const { selection, totalSelection, isSelectAllMode } = useSelectModeState();

  const { selectMode } = useBookingEditModeStoreActions();

  const bookingPastDaysLimit = useBookingPastDaysLimit();
  const { formatISODate } = useDateFormatter();

  const preparePayload = (filters: BulkOperationFilter[], exclude?: Exclude) => {
    const newExclude = {
      ...exclude,
      id: exclude?.id && exclude.id.length > 0 ? exclude.id : undefined
    };

    return {
      filters,
      ...(exclude ? { exclude: newExclude } : {}),
      dateRange: {
        min: formatISODate(utc().subtract(bookingPastDaysLimit, "days")),
        max: formatISODate(UNDETERMINED_FUTURE)
      },
      page: 1,
      perPage: PER_PAGE_UNPAGINATED
    };
  };

  const { data: selectableBookingsData, dataUpdatedAt: selectableBookingsDataUpdatedAt } =
    useBookingsForUpdate("total_selection", preparePayload(totalSelection?.filters ?? []), {
      enabled: !!totalSelection?.filters,
      placeholderData: (previousData) => (totalSelection?.filters ? previousData : undefined),
      gcTime: 0
    });

  useEffect(() => {
    if (!selectableBookingsData) {
      return;
    }
    if (!selection && isSelectAllMode) {
      selectMode.addSelectionEvent({
        type: "CLEAR_SELECTION",
        timestamp: Date.now()
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableBookingsDataUpdatedAt]);

  const { isLoading, data: bookingsForUpdateData } = useBookingsForUpdate(
    "filtered_selection",
    preparePayload(selection?.filters ?? [], selection?.exclude),
    {
      enabled: !!selection && !!selectableBookingsData,
      placeholderData: (previousData) =>
        !!selection && !!selectableBookingsData && selection.filters.length > 0 ? previousData : undefined,
      gcTime: 0
    }
  );

  const selectableBookingIds = useMemo(
    () =>
      isEditModeSelectOrDraft(editMode) ? selectableBookingsData?.entries.map(({ id }) => id) : undefined,
    [editMode, selectableBookingsData?.entries]
  );

  const filteredBookings = useMemo(
    () => (isEditModeSelectOrDraft(editMode) ? bookingsForUpdateData?.entries : undefined),
    [editMode, bookingsForUpdateData?.entries]
  );

  const categoriesForUpdateIds = useMemo(
    () =>
      isEditModeSelectOrDraft(editMode)
        ? uniq(bookingsForUpdateData?.entries.map(({ bookingCategoryId }) => bookingCategoryId))
        : undefined,
    [editMode, bookingsForUpdateData?.entries]
  );

  const bookingsTotal = bookingsForUpdateData?.meta.total;
  const bookingsCount = useMemo(
    () => (isEditModeSelectOrDraft(editMode) ? bookingsTotal : undefined),
    [editMode, bookingsTotal]
  );

  const minDateRange = bookingsForUpdateData?.meta.dateRange?.min ?? null;
  const bookingsRangeMinDate = useMemo(
    () => (isEditModeSelectOrDraft(editMode) ? minDateRange : null),
    [editMode, minDateRange]
  );

  const maxDateRange = bookingsForUpdateData?.meta.dateRange?.max ?? null;
  const bookingsRangeMaxDate = useMemo(
    () => (isEditModeSelectOrDraft(editMode) ? maxDateRange : null),
    [editMode, maxDateRange]
  );

  useEffect(() => {
    selectMode.setMetadata({
      bookingsCount,
      bookingsRangeMaxDate,
      bookingsRangeMinDate,
      selectableBookingIds: selectableBookingIds ?? [],
      filteredBookings: filteredBookings ?? [],
      filteredBookingsCategoriesIds: categoriesForUpdateIds ?? []
    });
  }, [
    bookingsCount,
    bookingsRangeMaxDate,
    bookingsRangeMinDate,
    selectableBookingIds,
    filteredBookings,
    categoriesForUpdateIds
  ]);

  useEffect(() => {
    selectMode.setIsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!isEditModeSelectOrDraft(editMode)) {
      selectMode.reset();
    }
  }, [editMode]);

  return {
    selectableBookingIds,
    filteredBookings,
    isLoading
  };
};
